// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The fail-closed gate banner. Restates, on the client, exactly the condition
// approve_reporting_gate re-derives server-side: the gate is unsatisfiable while
// reconciliation fails OR any blocking flag is unresolved. The client mirror is UX only —
// the RPC is the real wall — but it tells the reviewer WHY the button is disabled.

import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";
import type { Flag, IR } from "@/types/ir";

export interface GateReadiness {
  reconciliationPass: boolean;
  blockingFlags: Flag[];
  gateApproved: boolean;
  ready: boolean;
}

// Pure derivation the banner + GateTab share. Never flips the gate — only explains it.
export function deriveGateReadiness(ir: IR | undefined, gateApproved: boolean): GateReadiness {
  const reconciliationPass = ir?.attribution?.reconciliation?.all_pass === true;
  const blockingFlags = (ir?.flags ?? []).filter((f) => f.blocks_gate && !f.resolved);
  const ready = reconciliationPass && blockingFlags.length === 0;
  return { reconciliationPass, blockingFlags, gateApproved, ready };
}

export function GateStatusBanner({
  readiness,
  className,
}: {
  readiness: GateReadiness;
  className?: string;
}) {
  const { reconciliationPass, blockingFlags, gateApproved, ready } = readiness;

  const tone = gateApproved ? "good" : ready ? "ready" : "blocked";
  const Icon = gateApproved ? ShieldCheck : ready ? ShieldCheck : ShieldX;
  const border =
    tone === "good"
      ? "border-primary/40 bg-primary/5"
      : tone === "ready"
        ? "border-amber-500/40 bg-amber-500/5"
        : "border-destructive/40 bg-destructive/5";
  const iconTone = tone === "blocked" ? "text-destructive" : tone === "ready" ? "text-amber-600 dark:text-amber-400" : "text-primary";

  const title = gateApproved
    ? "Gate approved"
    : ready
      ? "Ready to sign — no blocking conditions remain"
      : "Gate blocked — sign-off is unsatisfiable";

  return (
    <Card className={cn("p-4 flex items-start gap-3", border, className)}>
      <Icon className={cn("h-5 w-5 mt-0.5 shrink-0", iconTone)} aria-hidden />
      <div className="min-w-0 space-y-1">
        <div className="font-heading text-base font-medium text-foreground">{title}</div>
        <ul className="font-body text-xs text-muted-foreground space-y-0.5">
          <li className="flex items-center gap-1.5">
            {reconciliationPass ? (
              <ShieldCheck className="h-3 w-3 text-primary" aria-hidden />
            ) : (
              <ShieldAlert className="h-3 w-3 text-destructive" aria-hidden />
            )}
            Reconciliation {reconciliationPass ? "passes" : "does NOT pass"}
          </li>
          <li className="flex items-center gap-1.5">
            {blockingFlags.length === 0 ? (
              <ShieldCheck className="h-3 w-3 text-primary" aria-hidden />
            ) : (
              <ShieldAlert className="h-3 w-3 text-destructive" aria-hidden />
            )}
            {blockingFlags.length === 0
              ? "No unresolved blocking flags"
              : `${blockingFlags.length} unresolved blocking flag(s): ${blockingFlags.map((f) => f.id).join(", ")}`}
          </li>
        </ul>
      </div>
    </Card>
  );
}
